import { curry, setKey, type PlatformEvent } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import { hide, show } from 'lib/sequence/component/state/hidden'
import showToast from 'app/_shared/component/showToast'
import {
  GET_FILE,
  STATUS,
  FILE_TYPE,
  MAXIMUM_FILE_BLOCKS,
  EXPECTED_FILE_BLOCKS,
} from 'app/xEcoApm/helper/apm'
import { parseFile, scale } from 'app/xEcoApm/helper'
import { set } from 'lib/sequence/component/state/value'
import { setData } from 'lib/sequence/model/api/set'
import routeComposition from 'trait/composition/route'
import refresh from 'lib/sequence/model/api/refresh'
import redirectSequence from 'lib/sequence/module/adapter/router/redirect'

type Measurement = {
  rawdata?: {
    frequency?: number[],
    cavitation?: number[],
  }
  results?: {
    frequency?: number[],
    apm?: number[],
    time?: number[],
  }
}

type FileType = number

type FileProgress<FileType> = {
  type: FileType,
  blocks: number,
}

type FileTransfer<FileType> = {
  type: FileType,
  currentBlock: number,
  totalBlocks: number,
  downloading: boolean,
}

type Transfer = {
  progress: Array<FileProgress<FileType>>
  current: FileTransfer<FileType> | null,
  elapsedTime: number,
}

type BindingProxyTarget<T> = {
  target: T,
  onPropertySet: (target: T) => void,
}

type ProgressState = {
  percent: string,
  remaining: number,
  smoothness: number,
  estimatedRemainingTime: number,
  lastProgressUpdateTime: number,
}

const initialState: ProgressState = {
  percent: '0.0',
  remaining: 0,
  smoothness: 0,
  estimatedRemainingTime: 0,
  lastProgressUpdateTime: 0,
}

/**
 * Creates a `Proxy` of `options.target` that triggers `options.onPropertySet` whenever one of its
 * properties is set.
 *
 * @param options configuration
 * @constructor
 */
const BindingProxy = <T extends object>(options: BindingProxyTarget<T>): T => {
  return new Proxy<T>(options.target, {
    get(target: T, name) {
      return target[name]
    },
    set(target: T, name, value) {
      target[name] = value
      options.onPropertySet(target)
      return true
    }
  })
}

/**
 * Displays a toast informing the user that the process couldn't be completed due to an error.
 *
 * @param module  current module
 */
const showErrorToast = async (module: Gaia.AppModule.Spec) => await showToast(module, {
  severity: 'error',
  message: {
    ns: 'apmGo',
    key: 'toast.report.error.message',
    defaultValue: 'Report generation error'
  },
  description: {
    ns: 'apmGo',
    key: 'toast.report.error.description',
    defaultValue: 'Please, check the device\'s connectivity and try again.'
  }
})

/**
 * Shows a toast informing the user that a measurement is done.
 *
 * @param module current module
 */
const showDoneToast = async (module: Gaia.AppModule.Spec) => await showToast(module, {
  message: {
    ns: 'apmGo',
    key: 'toast.report.success.message',
    defaultValue: 'Report generation finished'
  },
})

/**
 * Shows a toast informing the user that a measurement has been cancelled.
 *
 * @param module current module
 */
const showCancelToast = async (module: Gaia.AppModule.Spec) => await showToast(module, {
  message: {
    ns: 'apmGo',
    key: 'toast.report.cancel.message',
    defaultValue: 'Report generation cancelled'
  },
})

/**
 * Updates the current action's ui according to the `state` object.
 *
 * @param module  current module
 * @param state   ui state representation
 */
const updateUI = async (module: Gaia.AppModule.Spec, state: ProgressState) => {
  const intl = module[G.ADAPTER][G.INTL]
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { progressBar, completed: completedText, remaining: remainingText } = find(actionComponent)
  const { percent, remaining } = state

  const completedTextValue = intl._t('label.completedPercent', {
    ns: 'apmGo',
    _key: 'label.completedPercent',
    defaultValue: '{{number}} %',
    number: percent,
  })

  const remainingTextValue = intl._t('label.remainingSeconds', {
    ns: 'apmGo',
    _key: 'label.remainingSeconds',
    defaultValue: '{{number}} Seconds',
    number: remaining,
  })

  set(progressBar, parseInt(percent))
  set(completedText, completedTextValue)
  set(remainingText, remainingTextValue)

  await module[G.ADAPTER][G.UI].update(module)
}

/**
 * Resets process' start and stop state flags.
 *
 * @param module    current module
 * @param cancelled whether the process was manually cancelled
 */
const resetState = (module: Gaia.AppModule.Spec, cancelled: boolean) => {
  const actionState = module[G.STATE][G.ACTION][G.STATE]
  actionState.onReportStopped?.(cancelled)
  delete actionState.generatingReport
  delete actionState.stopReport
  delete actionState.onReportStopped
}

/**
 * Derives UI state from the current `state` and the `transfer` values.
 *
 * @param transfer  current transfer information
 * @param state     latest UI state
 * @returns         new state
 */
const updateState = (transfer: Transfer, state: ProgressState = initialState) => {
  const { progress, current: currentTransfer, elapsedTime } = transfer

  const newState = initialState

  const finishedTypes = new Set(progress.map(f => f.type))
  const actualFinishedBlocks = progress.reduce((sum, f) => sum + f.blocks, 0)

  const estimatedRemainingFiles = [FILE_TYPE.SPEC, FILE_TYPE.CAVI, FILE_TYPE.FREQ, FILE_TYPE.APM]
    .filter(ft => !finishedTypes.has(ft) && ft !== currentTransfer?.type)

  const estimatedTotalBlocks = actualFinishedBlocks
    + (transfer.current ? Math.max(transfer.current.currentBlock + 1, EXPECTED_FILE_BLOCKS[transfer.current.type]) : 0)
    + estimatedRemainingFiles.reduce((sum: number, ft) => sum + EXPECTED_FILE_BLOCKS[ft], 0)

  let downloadedBlocks = actualFinishedBlocks + (currentTransfer?.currentBlock ?? 0);

  const perBlock = elapsedTime / (downloadedBlocks || 1)
  // Exponential moving average to smooth per-block estimate
  newState.smoothness = state.smoothness === 0
    ? perBlock
    : 0.8 * state.smoothness + 0.2 * perBlock
  const remainingBlocks = estimatedTotalBlocks - downloadedBlocks
  newState.estimatedRemainingTime = Math.round((remainingBlocks * newState.smoothness) / 1000)
  const now = Date.now()

  if (now - state.lastProgressUpdateTime >= 1000) {
    const newPercent = ((downloadedBlocks / (estimatedTotalBlocks || 1)) * 100).toFixed(1);
    const newRemaining = newState.estimatedRemainingTime
    if (newPercent !== state.percent || newRemaining !== state.remaining) {
      newState.percent = newPercent
      newState.remaining = newRemaining
    }
    newState.lastProgressUpdateTime = now
  }

  return newState
}

/**
 * Reads a file in chunks from a BLE peripheral while keeping the UI in sync with the process.
 *
 * @param module    current module
 * @param transfer  current transfer
 * @param fileType  type of file do read
 * @param blocks    maximum file blocks to read
 * @returns         obtained file data
 */
const readFile = async (
  module: Gaia.AppModule.Spec,
  transfer: Transfer,
  fileType: FileType,
  blocks: number
): Promise<any> => {
  const actionState = module[G.STATE][G.ACTION][G.STATE]
  const bluetoothAdapter = module[G.ADAPTER][G.BLUETOOTH]
  const { sensor: model } = module[G.MODEL][G.CHILDREN]
  const { value: { name } } = model[G.CACHE]
  const peripheral = bluetoothAdapter[G.GROUP][name]

  const blockData: string[] = [];

  let limit = 0
  for (let block = 0; block < blocks; block++) {

    if (actionState.stopReport) {
      throw new Error('Report generation stopped by the user', { cause: G.USER })
    }

    try {
      // console.log(`Fetching block ${block} for file type ${fileType} from device ${key}`)
      transfer.current = {
        type: fileType,
        currentBlock: block,
        totalBlocks: 0,
        downloading: true,
      }

      const currentBlockData = await peripheral[G.API].send(GET_FILE({ type: fileType, block }))

      if (currentBlockData.s === STATUS.OUT_OF_RANGE) {
        console.error(`Block ${block} is out of range for file type ${fileType}. Stopping fetch.`);
        return
      }

      blockData[block] = currentBlockData.d

      if (currentBlockData.s === STATUS.EOF) {
        console.log(`EOF reached for file type ${fileType} at block ${block}.`);
        return { data: blockData.join(''), blockCount: block + 1 };
      }
    } catch(e) {
      console.error(`Error fetching block ${block} for file type ${fileType}:`, e);
      if (limit < 5) {
        console.log('Retrying')
        limit++
        block--
      } else {
        throw Error('Limit of 5 retries reached')
      }
    } finally {
      transfer.current = transfer.current ? {
        ...transfer.current,
        downloading: false,
      } : null
    }
  }

  console.warn(`Reached max blocks (${blocks}) without EOF for file type ${fileType}.`);
  return { data: blockData.join(''), blockCount: blocks };
}

/**
 * Attempts to read all measurement files from the current bluetooth peripheral (current model's
 * `sensor`), which are written in the device during a `MEAS_CTRL_ON` process.
 *
 * Keeps the UI in sync with the process.
 *
 * @param module  current module
 * @returns       obtained measurement data
 */
const readFiles = async (module: Gaia.AppModule.Spec): Promise<Measurement> => {
  const report: Measurement = {}

  let state: ProgressState = {
    percent: '0.0',
    remaining: 0,
    smoothness: 0,
    estimatedRemainingTime: 0,
    lastProgressUpdateTime: 0,
  }

  // Binding UI updates to changes to properties in the `Transfer` object
  const transfer = BindingProxy({
    target: { progress: [], current: null, elapsedTime: 0 } as Transfer,
    onPropertySet: async (target) => {
      state = updateState(target, state)
      await updateUI(module, state)
    }
  })

  const startTime = new Date()

  const timeInterval = setInterval(() => {
    transfer.elapsedTime = new Date().getTime() - startTime?.getTime()
  }, 1000)

  try {
    const { scalingFactor } = module[G.STATE]

    const specResult = await readFile(module, transfer, FILE_TYPE.SPEC, MAXIMUM_FILE_BLOCKS[FILE_TYPE.SPEC])
    if (specResult) {
      const parsedSpec = parseFile(specResult.data)?.map(num => scale(num, scalingFactor))
      report.rawdata = { ...(report.rawdata || {}), frequency: parsedSpec }
      transfer.progress = [...transfer.progress, { type: FILE_TYPE.SPEC, blocks: specResult.blockCount }]
    }

    const caviResult = await readFile(module, transfer, FILE_TYPE.CAVI, MAXIMUM_FILE_BLOCKS[FILE_TYPE.CAVI])
    if (caviResult) {
      const parsedCavi = parseFile(caviResult.data)?.map(num => scale(num, scalingFactor))
      report.rawdata = { ...(report.rawdata || {}), cavitation: parsedCavi }
      transfer.progress = [...transfer.progress, { type: FILE_TYPE.CAVI, blocks: caviResult.blockCount }]
    }

    const freqResult = await readFile(module, transfer, FILE_TYPE.FREQ, MAXIMUM_FILE_BLOCKS[FILE_TYPE.FREQ])
    if (freqResult) {
      const parsedFreq = parseFile(freqResult.data) || []
      report.results = { ...(report.results || {}), frequency: parsedFreq, time: parsedFreq.map((_, i) => i) }
      transfer.progress = [...transfer.progress, { type: FILE_TYPE.FREQ, blocks: freqResult.blockCount }]
    }

    const apmResult = await readFile(module, transfer, FILE_TYPE.APM, MAXIMUM_FILE_BLOCKS[FILE_TYPE.APM])
    if (apmResult) {
      const parsedApm = parseFile(apmResult.data)
      report.results = { ...(report.results || {}), apm: parsedApm }
      transfer.progress = [...transfer.progress, { type: FILE_TYPE.SPEC, blocks: apmResult.blockCount }]
    }
  } catch (error: any) {
    transfer.progress = []
    throw error
  } finally {
    clearInterval(timeInterval)
  }

  return report
}

/**
 * Sets the `report` route as the model's route and calls `redirectSequence`.
 *
 * @param module  current module
 */
const redirectToReport = async (module: Gaia.AppModule.Spec) => {
  setKey(routeComposition(null, 'report'), G.ROUTE, module[G.STATE])
  delete module[G.STATE].scalingFactor
  await redirectSequence(module)({})
}

/**
 * Saves `measuredData` in the `measurement` model.
 *
 * @param module        current module
 * @param measuredData  measurement result
 */
const saveMeasurementData = async (module: Gaia.AppModule.Spec, measuredData: Measurement)=> {
  const model = module[G.MODEL]
  const { measurement } = model[G.CHILDREN]

  setData(measurement, { measuredData }, { updateCache: true })
  await refresh(measurement)
}

/**
 * Generates a measurement report.
 *
 * Retrieves measurement files in chunks from a bluetooth peripheral (current model's `sensor`).
 * Synchronizes action's UI with retrieval progress.
 * Uses obtained data to fill `measuredData` in `measurement` model.
 * Redirects to `report` route.
 *
 * @param module    current module
 * @param component event source
 * @param event     triggered event
 */
const generateReport = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec | null,
  event: PlatformEvent | null
) => {
  const action = module[G.STATE][G.ACTION]
  const { btnStart, btnStop } = find(action[G.COMPONENT][G.ACTIONS][0])
  const actionState = action[G.STATE]

  resetState(module, false)
  actionState.generatingReport = true

  try {
    hide(btnStart)
    show(btnStop)
    await module[G.ADAPTER][G.UI].update(module)

    const measurement = await readFiles(module)

    resetState(module, false)

    await showDoneToast(module)
    await saveMeasurementData(module, measurement)
    await redirectToReport(module)

  } catch (error: any) {
    const cancelled = error.cause === G.USER

    hide(btnStop)
    show(btnStart)
    await module[G.ADAPTER][G.UI].update(module)

    resetState(module, cancelled)

    cancelled
      ? await showCancelToast(module)
      : await showErrorToast(module)

    throw new Error('Error generating report', { cause: error })
  }
}

export default curry(generateReport)
